import type ApexCharts from 'apexcharts'
import type { ApexOptions } from 'apexcharts'

let ApexChartsCtor: typeof ApexCharts | null = null
async function loadApexCharts(): Promise<typeof ApexCharts> {
    if (!ApexChartsCtor) {
        ApexChartsCtor = (await import('apexcharts')).default
    }
    return ApexChartsCtor
}

const parseUtc = (s: string) => new Date(/^\d{4}-\d{2}-\d{2} /.test(s) ? s.replace(' ', 'T') + 'Z' : s)

/**
 * Dynamic "Live Curve Preview" chart.
 *
 * Replaces the static demo SVG with a live ApexCharts area chart fed by
 * GET /api/curves/preview. The API speaks the Granularity enum (hour|day|month)
 * and returns the native CurveSnapshot shape, so this module maps the UI tabs
 * to those resolutions and derives min/max + published date client-side.
 *
 * Navigation uses ApexCharts' brush feature as a slim scroll bar: a short
 * navigator strip sits directly under the main chart, and its draggable
 * selection window pans/zooms the main chart via `brush.target`. The strip is
 * styled as a scroll bar (no axis labels, faint trace, prominent handle), not a
 * second graph. We let ApexCharts own the zoom entirely — driving the series
 * manually from brush events fights its auto-zoom and corrupts the render.
 */

type Resolution = 'hour' | 'day' | 'month'

interface CurvePoint {
    date: string
    value: number
}

interface CurveSnapshot {
    trading_date: string
    granularity: Resolution | string
    points: CurvePoint[]
    fetched_at: string
}

const ENDPOINT = '/api/curves/preview'
const BRAND = '#e84757'
const GRID = '#e8dddd'
const AXIS_TEXT = '#94a3b8'
const SERIES_NAME = 'Nord Pool HPFC'
const MAIN_ID = 'curve-preview-main'
const BRUSH_ID = 'curve-preview-brush'
/** Navigator height — a compact mini-chart (curve + date axis), ApexCharts brush style. */
const NAV_HEIGHT = 96
/**
 * Main-series point cap. Deliberately high so the default hourly/daily/monthly
 * windows keep their native resolution and the brush zoom reveals real points
 * without re-bucketing. Only a pathologically large full series is thinned, and
 * even then only by dropping whole real points — never by moving timestamps.
 */
const MAIN_CAP = 20_000
/** Full-range overview trace shown in the navigator scroll bar. */
const NAV_POINTS = 400
/**
 * Deepest zoom = this many native steps in view. At 3 steps the viewport spans
 * ~3 cadence intervals (≈4 real points) — enough to read the curve's shape
 * while staying above the data's own granularity, so the most zoomed-in view
 * can never collapse into a single interpolated diagonal that pretends to be
 * finer-than-native data. The floor span is `MIN_VISIBLE_STEPS × nativeStep`.
 */
const MIN_VISIBLE_STEPS = 3
const DAY_MS = 86_400_000
const MONTH_MS = 30 * DAY_MS
const YEAR_MS = 365 * DAY_MS

/**
 * Initial brush window per resolution, anchored at the start of the range:
 * hourly opens on the first month, daily on the first three months, monthly on
 * the first year — clamped to whatever the data actually spans. Returns
 * `[min, max]` timestamps for the selection.
 */
export function initialSelection(
    allData: [number, number][],
    resolution: Resolution,
): [number, number] {
    const start = allData[0][0]
    const end = allData[allData.length - 1][0]
    const span = resolution === 'hour' ? MONTH_MS : resolution === 'day' ? 3 * MONTH_MS : YEAR_MS
    return [start, Math.min(end, start + span)]
}

/**
 * Loading-presentation rule for a (re)load of the preview.
 *
 * Given whether a chart is *currently rendered and on screen*, decide whether to
 * swap in the shimmer skeleton (hiding whatever is there) or to leave the current
 * frame in place while the next resolution fetches:
 *
 * - No chart on screen — first load, or recovering from an error / empty /
 *   unavailable state — there is nothing to keep, so show the skeleton.
 * - A chart is already on screen (a genuine resolution-switch) — keep it visible
 *   during the fetch so the swap reads as a smooth transition, not a blank flash.
 *
 * Kept as a pure function (no DOM) so the rule is testable in isolation.
 */
export function shouldShowSkeleton(chartVisible: boolean): boolean {
    return !chartVisible
}

/**
 * Thin a series to at most `maxPoints` while keeping every emitted point a
 * *real* source point at its *true* timestamp — the chart must faithfully
 * reflect the API payload, so we never average timestamps or values into
 * synthetic positions. Returns the input untouched when it already fits (the
 * common case once `maxPoints` is high), otherwise selects points on a uniform
 * index stride and always keeps the first and last so the visible extent is
 * unchanged.
 */
export function prepareSeries(data: [number, number][], maxPoints: number): [number, number][] {
    if (data.length <= maxPoints) {
        return data
    }

    // Keep first + last; stride the interior so we land on ~maxPoints real points.
    const stride = Math.ceil((data.length - 1) / (maxPoints - 1))
    const result: [number, number][] = []
    for (let i = 0; i < data.length; i += stride) {
        result.push(data[i])
    }
    const last = data[data.length - 1]
    if (result[result.length - 1] !== last) {
        result.push(last)
    }
    return result
}

/**
 * The series' native cadence: the **median** spacing between consecutive
 * timestamps. Median (not mean) makes it robust to data gaps and DST clock
 * steps — a single oversized or zero-width step never drags the result — and it
 * adapts automatically to a half-hourly grid. Returns `0` when there is no
 * spacing to measure (fewer than two points).
 */
export function nativeStep(data: [number, number][]): number {
    if (data.length < 2) {
        return 0
    }

    const deltas: number[] = []
    for (let i = 1; i < data.length; i++) {
        deltas.push(data[i][0] - data[i - 1][0])
    }
    deltas.sort((a, b) => a - b)
    return deltas[Math.floor(deltas.length / 2)]
}

/**
 * Clamp a candidate visible window `[min, max]` so it never zooms below the
 * data's own native resolution — the single source of truth for the zoom floor.
 * `minRange` is the narrowest span the viewport may show (see
 * `MIN_VISIBLE_STEPS × nativeStep`), and `[dataMin, dataMax]` is the full
 * series extent. Contract:
 *
 *   - if `minRange >= (dataMax - dataMin)` the floor is wider than all the data,
 *     so just show everything → `[dataMin, dataMax]`;
 *   - otherwise clamp `[min, max]` into the extent; if it is already at least
 *     `minRange` wide, return it (clamped) unchanged;
 *   - else it is below the floor: widen it to **exactly** `minRange`, centred on
 *     its own midpoint, then slide it wholesale to stay inside the extent when
 *     centring would overflow `dataMin` (start) or `dataMax` (end).
 */
export function clampVisibleRange(
    min: number,
    max: number,
    minRange: number,
    dataMin: number,
    dataMax: number,
): [number, number] {
    // Defensive: a non-finite candidate window (NaN/undefined from a degenerate
    // selection) has no meaningful clamp — fall back to the full extent rather
    // than propagate NaN into the axis.
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return [dataMin, dataMax]
    }

    const extent = dataMax - dataMin
    if (minRange >= extent) {
        return [dataMin, dataMax]
    }

    // Clamp the candidate window into the extent first.
    let lo = Math.max(dataMin, Math.min(min, dataMax))
    let hi = Math.max(dataMin, Math.min(max, dataMax))

    if (hi - lo >= minRange) {
        return [lo, hi]
    }

    // Below the floor: widen to exactly minRange centred on the midpoint, then
    // shift the whole window in if it overflows either edge of the extent.
    const mid = (lo + hi) / 2
    lo = mid - minRange / 2
    hi = mid + minRange / 2
    if (lo < dataMin) {
        lo = dataMin
        hi = dataMin + minRange
    } else if (hi > dataMax) {
        hi = dataMax
        lo = dataMax - minRange
    }
    return [lo, hi]
}

/** A spacing wider than this multiple of the median spacing is a genuine gap. */
const GAP_FACTOR = 1.5

/**
 * Upper bound (× median spacing) for treating a single oversized step on an
 * intraday grid as a DST clock change rather than a data gap. The KYOS `hour`
 * grid is DST-aware local time (CET/CEST), so spring-forward skips exactly one
 * slot: e.g. 01:00 → 03:00 reads as a 2× step. Anything up to this multiple on
 * an intraday resolution is a clock change (suppressed); a genuinely-missing
 * block of hours is wider than this and still breaks. Day/month grids have no
 * DST steps, so this never applies to them (a 2× daily step is a missing day
 * and must break).
 */
const DST_MAX_FACTOR = 2.5

/**
 * Insert a `null`-valued break point into every genuine gap so ApexCharts
 * draws a line *break* there rather than a misleading smooth diagonal bridging
 * two non-adjacent points (e.g. 01:30 → 05:00, or 28 June → 30 June). A gap is
 * any spacing wider than `GAP_FACTOR` × the median spacing of the series, which
 * adapts to the native cadence (hourly, half-hourly, daily) without a hardcoded
 * step. An evenly-spaced series is returned unchanged.
 *
 * On intraday resolutions (`hour`), a Daylight-Saving clock change skips (spring)
 * or repeats (autumn) one slot, which would otherwise read as a one-slot gap or a
 * zero-width duplicate. Those are clock artifacts, NOT missing data, so they are
 * not broken: a single step up to `DST_MAX_FACTOR` × median is left intact. Real
 * missing blocks (wider than that) still break.
 */
export function markGaps(
    data: [number, number][],
    resolution: Resolution = 'hour',
): [number, number | null][] {
    if (data.length < 3) {
        return data
    }

    const deltas: number[] = []
    for (let i = 1; i < data.length; i++) {
        deltas.push(data[i][0] - data[i - 1][0])
    }
    const sorted = [...deltas].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    const threshold = median * GAP_FACTOR
    // Intraday grids carry DST clock changes; daily/monthly never do.
    const dstCeiling = resolution === 'hour' ? median * DST_MAX_FACTOR : 0

    const result: [number, number | null][] = [data[0]]
    for (let i = 1; i < data.length; i++) {
        const prevTs = data[i - 1][0]
        const ts = data[i][0]
        const span = ts - prevTs
        // A single DST-sized step on an intraday grid is a clock change, not a
        // gap — bridge it as normal rather than inserting a false break.
        const isDstStep = span > threshold && span <= dstCeiling
        if (span > threshold && !isDstStep) {
            // Break point at the midpoint of the gap; its null y splits the line.
            result.push([prevTs + span / 2, null])
        }
        result.push(data[i])
    }
    return result
}

const RESOLUTION_LABEL: Record<Resolution, string> = {
    hour: 'Hourly',
    day: 'Daily',
    month: 'Monthly',
}

const utc = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' })

const hourBoundaryFormatter = utc({ day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
const dayBoundaryFormatter = utc({ day: '2-digit', month: 'short', year: 'numeric' })
const monthBoundaryFormatter = utc({ month: 'short', year: 'numeric' })

/** Human-readable boundary label for the meta panel (Start / End rows). */
function boundaryLabel(iso: string, resolution: Resolution): string {
    const d = parseUtc(iso)
    if (Number.isNaN(d.getTime())) return iso

    if (resolution === 'hour') {
        return hourBoundaryFormatter.format(d)
    }
    if (resolution === 'day') {
        return dayBoundaryFormatter.format(d)
    }
    return monthBoundaryFormatter.format(d)
}

interface DatetimeFormatter {
    year?: string
    month?: string
    day?: string
    hour?: string
    minute?: string
    second?: string
}

/**
 * Per-resolution axis tick formatting (main chart and navigator strip).
 *
 * Every tier *finer* than the active resolution is pinned to that resolution's
 * own format — the second line of defence behind the zoom floor. Even if a tick
 * ever lands inside the floor, the axis can never label a unit finer than the
 * data's granularity: Hourly never prints real `:mm`/`:ss` minutes, Daily never
 * drops to hours, Monthly never drops to days. The `minute`/`second` tiers are
 * therefore filled (ApexCharts would otherwise fall back to its own
 * `HH:mm:ss`-style defaults for them, which is exactly the misleading label the
 * bug report flags).
 *
 * The collapse works by *literal* formatting, not by truncating the timestamp:
 * a finer tier renders the parent unit's tokens followed by a hard-coded
 * lower-order constant. The KEY point is that `'HH:00'` prints the literal text
 * `00` for the minutes (`formatDate` passes non-token characters through
 * verbatim) — it does NOT read the real minute. So when the zoom floor lets
 * ApexCharts subdivide a 3-hour hourly window into 30-minute ticks (07:30,
 * 08:00, 08:30 …), every sub-hour tick renders as its *containing hour*
 * (`07:30 → "07:00"`). Adjacent ticks then collapse to identical strings, and
 * ApexCharts' duplicate-label suppression (`xaxis.labels.showDuplicates` is
 * false by default) blanks the repeats, leaving clean on-the-hour labels
 * (07:00 · 08:00 · 09:00). This mirrors how Daily/Monthly already dedupe their
 * own sub-unit ticks; a naive `'HH:mm'` here instead prints the true minute and
 * is what surfaced the half-hour labels on the hourly curve.
 */
export function datetimeFormatterFor(resolution: Resolution): DatetimeFormatter {
    if (resolution === 'hour') {
        // Floor at the hour: minute/second tiers collapse to the hour label
        // (literal ':00'), so a subdivided window never labels a half-hour.
        return { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM', hour: 'HH:mm', minute: 'HH:00', second: 'HH:00' }
    }
    if (resolution === 'day') {
        // Floor at the day: hour and finer tiers collapse to the day label.
        return { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM', hour: 'dd MMM', minute: 'dd MMM', second: 'dd MMM' }
    }
    // Floor at the month: day and finer tiers collapse to the month label.
    return { year: 'yyyy', month: "MMM 'yy", day: "MMM 'yy", hour: "MMM 'yy", minute: "MMM 'yy", second: "MMM 'yy" }
}

function tooltipDateFormatter(resolution: Resolution): (timestamp: number) => string {
    if (resolution === 'hour') {
        return (ts) => {
            const d = new Date(ts)
            return utc({ day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(d) + ' UTC'
        }
    }
    if (resolution === 'day') {
        return (ts) => utc({ day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(ts))
    }
    return (ts) => utc({ month: 'long', year: 'numeric' }).format(new Date(ts))
}

/**
 * Per-resolution zoom floor: the narrowest span the viewport may show, the
 * single number `clampVisibleRange` enforces. `MIN_VISIBLE_STEPS` native steps
 * wide (median spacing × 3), so it adapts to the curve's real cadence (hourly,
 * half-hourly, daily) instead of a hardcoded per-resolution constant. Falls
 * back to `0` (no floor) when the series is too short to measure a step.
 */
function zoomFloorFor(allData: [number, number][]): number {
    return MIN_VISIBLE_STEPS * nativeStep(allData)
}

/**
 * Main chart options. The toolbar is hidden (the scroll bar is the only
 * navigator) but zoom stays enabled so `brush.target` can drive the viewport.
 *
 * `beforeZoom` is the floor's first enforcement point: it covers drag-to-zoom
 * directly on the plot by clamping the requested window through
 * `clampVisibleRange` and returning the clamped `[min, max]`, so the user can
 * never drag past the data's native resolution. (The brush navigator bypasses
 * this hook — see `navigatorOptions` for the second enforcement point.)
 */
function mainChartOptions(
    seriesData: [number, number | null][],
    resolution: Resolution,
    pointCount: number,
    minRange: number,
    dataMin: number,
    dataMax: number,
    selMin?: number,
    selMax?: number,
): ApexOptions {
    const formatTooltipDate = tooltipDateFormatter(resolution)
    return {
        chart: {
            id: MAIN_ID,
            type: 'area',
            height: 260,
            fontFamily: 'inherit',
            toolbar: { show: false },
            zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
            events: {
                // Returning `{ xaxis: { min, max } }` makes ApexCharts adopt
                // that window instead of the user's drag — the documented v5.14
                // runtime behaviour. The bundled type declares the hook as
                // `boolean | void` (it predates this), so the richer return is
                // flagged here; the suppression is scoped to exactly this line.
                // @ts-expect-error -- beforeZoom may return a clamped { xaxis } window at runtime
                beforeZoom: (_chart, opts) => {
                    if (!opts || minRange <= 0) {
                        return
                    }
                    const [min, max] = clampVisibleRange(
                        opts.xaxis.min,
                        opts.xaxis.max,
                        minRange,
                        dataMin,
                        dataMax,
                    )
                    return { xaxis: { min, max } }
                },
            },
            animations: {
                enabled: pointCount < 500,
                speed: 450,
                dynamicAnimation: { enabled: false },
            },
            parentHeightOffset: 0,
        },
        colors: [BRAND],
        series: [{ name: SERIES_NAME, data: seriesData }],
        stroke: { curve: 'straight', width: 2, lineCap: 'round' },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.15,
                opacityTo: 0,
                stops: [0, 100],
            },
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: GRID,
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { left: 4, right: 8, top: 0, bottom: 0 },
        },
        xaxis: {
            type: 'datetime',
            // Open on the initial brush window (undefined → auto-scale for
            // empty/single-point data); the brush re-zooms from here.
            min: selMin,
            max: selMax,
            tickAmount: Math.min(8, seriesData.length),
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                datetimeUTC: true,
                style: { colors: AXIS_TEXT, fontSize: '10px' },
                datetimeFormatter: datetimeFormatterFor(resolution),
            },
            tooltip: { enabled: false },
        },
        yaxis: {
            title: {
                text: 'EUR / MWh',
                style: { color: AXIS_TEXT, fontSize: '11px', fontWeight: 500 },
            },
            labels: {
                style: { colors: AXIS_TEXT, fontSize: '10px' },
                formatter: (v: number) => `${Math.round(v)}`,
            },
        },
        tooltip: {
            // Deliberately withhold the exact price at each point. Our source
            // exchanges (ICE/EEX/…) must not be able to read their monthly
            // settlement values off this preview and argue we redistribute
            // their prices — so the viewer gets the curve's *shape* and the
            // Y-axis scale, but never the precise price at a hovered point.
            // A custom tooltip renders the date only (which the x-axis and meta
            // panel already expose), keeping the "where am I on the curve"
            // hover cue without exposing a number. `custom` also overrides the
            // default y/value row entirely, so no price string can leak.
            custom: ({ dataPointIndex, w }) => {
                const ts = w.globals.seriesX?.[0]?.[dataPointIndex]
                const label =
                    typeof ts === 'number' && Number.isFinite(ts) ? formatTooltipDate(ts) : ''
                return `<div class="curve-preview-tooltip">${label}</div>`
            },
        },
        legend: { show: false },
    }
}

/**
 * Brush navigator options — the ApexCharts brush-chart look: a compact
 * full-range mini-chart (visible trace + date axis) with a grey dashed
 * selection window that pans/zooms the main chart through `brush.target`.
 * A matching y-axis keeps its plot aligned with the main chart above.
 * `selMin`/`selMax` set the initial window (the full range).
 */
function navigatorOptions(
    navData: [number, number | null][],
    resolution: Resolution,
    selMin: number,
    selMax: number,
    minRange: number,
    dataMin: number,
    dataMax: number,
    getMainChart: () => ApexCharts | null,
): ApexOptions {
    return {
        chart: {
            id: BRUSH_ID,
            type: 'area',
            height: NAV_HEIGHT,
            fontFamily: 'inherit',
            brush: { enabled: true, target: MAIN_ID, autoScaleYaxis: true },
            selection: {
                enabled: true,
                fill: { color: '#64748b', opacity: 0.1 },
                stroke: { width: 1, color: '#64748b', opacity: 0.6, dashArray: 3 },
                xaxis: { min: selMin, max: selMax },
            },
            events: {
                // Second enforcement point for the zoom floor — and, because we
                // define it, ALSO the brush→target drive itself.
                //
                // ApexCharts only auto-installs its default brush handler (which
                // pushes the selection onto the target via the target's internal
                // `_updateOptions({ xaxis })`, BYPASSING the target's
                // `beforeZoom`) when the navigator has NO `selection` handler of
                // its own (see ApexCharts 5.14 Core.setupBrushHandler). By
                // registering this handler we suppress that default, so we must
                // drive the main chart ourselves on EVERY selection — otherwise
                // the brush would stop panning/zooming the plot. We drive it with
                // the floor-clamped window, which is what makes the floor hold
                // for brush-zoom (the path `beforeZoom` cannot see), and we
                // re-seat the brush rectangle only when clamping actually changed
                // the window, so the handles snap to the floor and the plot and
                // navigator never disagree.
                selection: (navChart, opts) => {
                    if (!opts?.xaxis) {
                        return
                    }
                    const { min, max } = opts.xaxis
                    const [lo, hi] =
                        minRange > 0
                            ? clampVisibleRange(min, max, minRange, dataMin, dataMax)
                            : [min, max]
                    // Always drive the target (we replaced the default brush
                    // drive by defining this handler). zoomX is the public
                    // equivalent of the internal `_updateOptions({ xaxis })`.
                    getMainChart()?.zoomX(lo, hi)
                    // Re-seat the brush rectangle only when the floor actually
                    // widened the requested window (selection edges are
                    // pixel-derived floats, so compare with a sub-step
                    // tolerance, not exact equality). The re-seated window is
                    // exactly `minRange` wide, so a subsequent user drag is no
                    // longer below the floor — no runaway correction. (Note:
                    // updateOptions redraws the rect but does NOT re-fire the
                    // `selection` event, so there is no event loop either way.)
                    const tol = minRange > 0 ? minRange / 1000 : 1
                    if (Math.abs(lo - min) > tol || Math.abs(hi - max) > tol) {
                        navChart.updateOptions(
                            { chart: { selection: { xaxis: { min: lo, max: hi } } } },
                            false,
                            false,
                        )
                    }
                },
            },
            animations: { enabled: false },
            toolbar: { show: false },
            parentHeightOffset: 0,
        },
        colors: [BRAND],
        series: [{ name: SERIES_NAME, data: navData }],
        stroke: { curve: 'straight', width: 1.5, lineCap: 'round' },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: GRID,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: false } },
            padding: { left: 4, right: 8, top: 0, bottom: 0 },
        },
        legend: { show: false },
        xaxis: {
            type: 'datetime',
            tickAmount: Math.min(7, navData.length),
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                datetimeUTC: true,
                style: { colors: AXIS_TEXT, fontSize: '10px' },
                datetimeFormatter: datetimeFormatterFor(resolution),
            },
            tooltip: { enabled: false },
        },
        yaxis: {
            show: true,
            tickAmount: 2,
            labels: {
                style: { colors: AXIS_TEXT, fontSize: '10px' },
                formatter: (v: number) => `${Math.round(v)}`,
            },
        },
        tooltip: { enabled: false },
    }
}

function deriveMeta(snapshot: CurveSnapshot, resolution: Resolution): Record<string, string> {
    const points = snapshot.points
    const meta: Record<string, string> = {
        'trading-date': snapshot.trading_date.slice(0, 10),
        resolution: RESOLUTION_LABEL[resolution],
        published: `${boundaryLabel(snapshot.fetched_at, 'hour')} UTC`,
    }

    if (points.length > 0) {
        meta.start = boundaryLabel(points[0].date, resolution)
        meta.end = boundaryLabel(points[points.length - 1].date, resolution)
    }

    return meta
}

function initCurvePreview(): void {
    const section = document.querySelector<HTMLElement>('.section-preview')
    if (!section) return

    const chartEl = section.querySelector<HTMLElement>('#curve-preview-chart')
    const brushEl = section.querySelector<HTMLElement>('#curve-preview-brush')
    const statusEl = section.querySelector<HTMLElement>('#curve-preview-status')
    if (!chartEl || !brushEl || !statusEl) return

    const tabs = Array.from(section.querySelectorAll<HTMLButtonElement>('[data-resolution]'))
    const metaVals = section.querySelectorAll<HTMLElement>('.preview-meta-val[data-meta]')

    let mainChart: ApexCharts | null = null
    let navChart: ApexCharts | null = null
    let requestSeq = 0
    let zoomTimers: number[] = []

    const destroyCharts = () => {
        zoomTimers.forEach(clearTimeout)
        zoomTimers = []
        navChart?.destroy()
        mainChart?.destroy()
        navChart = null
        mainChart = null
    }

    const setMeta = (meta: Record<string, string>) => {
        metaVals.forEach((el) => {
            const key = el.dataset.meta
            el.textContent = key && meta[key] ? meta[key] : '—'
        })
    }

    const showStatus = (html: string) => {
        statusEl.innerHTML = html
        statusEl.hidden = false
        chartEl.hidden = true
        brushEl.hidden = true
    }

    const showChart = () => {
        statusEl.hidden = true
        chartEl.hidden = false
    }

    /** A chart is "currently on screen" only when an instance exists and its
     *  container is visible (an error/empty/unavailable state hides it). This is
     *  the input to `shouldShowSkeleton`. */
    const chartOnScreen = () => mainChart !== null && !chartEl.hidden

    /**
     * Begin a crossfade-in: mark the chart + navigator transparent so the
     * about-to-render frame starts at opacity:0. The `.curve-preview--entering`
     * class sets opacity:0; `endChartFadeIn` later removes it so CSS transitions
     * opacity → 1. Under `prefers-reduced-motion: reduce` that transition
     * collapses to 0ms (instant), so this is a pure enhancement.
     */
    const beginChartFadeIn = () => {
        chartEl.classList.add('curve-preview--entering')
        brushEl.classList.add('curve-preview--entering')
    }

    /**
     * Finish the crossfade: on the next frame, drop the `--entering` class so the
     * containers transition from opacity:0 to their default opacity:1. Two rAFs
     * guarantee the opacity:0 start state was committed before the transition is
     * triggered, so the fade actually runs instead of snapping. The
     * teardown+rebuild of the linked brush charts is unchanged — the smoothness is
     * this wrapper, not avoiding the rebuild.
     */
    const endChartFadeIn = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                chartEl.classList.remove('curve-preview--entering')
                brushEl.classList.remove('curve-preview--entering')
            })
        })
    }

    const render = async (snapshot: CurveSnapshot, resolution: Resolution, seq: number) => {
        if (seq !== requestSeq) return

        const allData = snapshot.points.map((p) => [parseUtc(p.date).getTime(), Number(p.value)] as [number, number])
        // Main chart: real points at native resolution (only thinned above a
        // very high cap), with genuine gaps marked as line breaks so nothing is
        // averaged away or bridged by a false diagonal.
        const mainData = markGaps(prepareSeries(allData, MAIN_CAP), resolution)
        // Navigator: a coarse overview is fine, but still on real timestamps and
        // honest about gaps.
        const navData = markGaps(prepareSeries(allData, NAV_POINTS), resolution)

        const ApexChartsCls = await loadApexCharts()
        if (seq !== requestSeq) return

        // Start the new frame transparent so it fades in once rendered, hiding
        // the destroy→rebuild flash whether the old chart was held on screen
        // (resolution-switch) or the skeleton was showing (first load).
        beginChartFadeIn()

        // Linked brush charts can't be re-bound cleanly via updateOptions, so
        // rebuild both instances on every (infrequent) tab switch / reload.
        destroyCharts()
        showChart()

        // A brush window only makes sense with a real range (>1 point). Empty or
        // single-point data renders an auto-scaled main chart with no navigator.
        const range = allData.length > 1 ? initialSelection(allData, resolution) : null
        // Zoom floor + extent shared by both enforcement points (main
        // `beforeZoom`, navigator `selection`). Derived from the data's own
        // median cadence so it adapts to hourly / half-hourly / daily grids.
        const minRange = zoomFloorFor(allData)
        const dataMin = allData.length > 0 ? allData[0][0] : 0
        const dataMax = allData.length > 0 ? allData[allData.length - 1][0] : 0
        mainChart = new ApexChartsCls(
            chartEl,
            mainChartOptions(mainData, resolution, allData.length, minRange, dataMin, dataMax, range?.[0], range?.[1]),
        )
        await mainChart.render()

        if (range) {
            const [selMin, selMax] = range
            // Unhide BEFORE rendering so ApexCharts measures a non-zero width
            // (a hidden element renders the navigator collapsed to 0px wide).
            brushEl.hidden = false
            // Render the navigator after the main chart so brush.target resolves.
            navChart = new ApexChartsCls(
                brushEl,
                navigatorOptions(navData, resolution, selMin, selMax, minRange, dataMin, dataMax, () => mainChart),
            )
            await navChart.render()
            // The brush resets the target to the full extent shortly after its
            // render, so re-apply the initial window a few times until it sticks
            // (zoomX is idempotent). Guarded so a later tab switch wins; timers
            // are cleared in destroyCharts.
            if (selMax < allData[allData.length - 1][0]) {
                const mc = mainChart
                for (const delay of [50, 200, 450]) {
                    zoomTimers.push(window.setTimeout(() => {
                        if (mc && seq === requestSeq) mc.zoomX(selMin, selMax)
                    }, delay))
                }
            }
        } else {
            brushEl.hidden = true
        }

        if (seq === requestSeq) {
            setMeta(deriveMeta(snapshot, resolution))
        }

        // Both instances are rendered: fade the new frame in to opacity 1.
        endChartFadeIn()
    }

    const load = async (resolution: Resolution) => {
        const seq = ++requestSeq

        // Differentiate first-load from a resolution-switch. With no chart on
        // screen (first load, or recovery from an error/empty/unavailable state)
        // show the shimmer skeleton. With a chart already showing, hold it in
        // place during the fetch so the swap reads as a smooth transition rather
        // than a blank flash; the meta stays too until the new render replaces it.
        if (shouldShowSkeleton(chartOnScreen())) {
            showStatus('<div class="curve-preview-skeleton" aria-hidden="true"></div>')
            setMeta({})
        }

        try {
            const res = await fetch(`${ENDPOINT}?resolution=${resolution}`, {
                headers: { Accept: 'application/json' },
            })

            if (seq !== requestSeq) return

            if (res.status === 503) {
                if (seq === requestSeq) {
                    showStatus(
                        '<div class="curve-preview-message">' +
                            '<span class="curve-preview-message-icon">⏳</span>' +
                            '<p>Latest curves will be available after midnight.</p>' +
                            '<span class="curve-preview-message-sub">Today\'s data is being processed.</span>' +
                            '</div>',
                    )
                    setMeta({})
                }
                return
            }

            if (res.status === 404) {
                if (seq === requestSeq) {
                    showStatus(
                        '<div class="curve-preview-message">' +
                            '<p>This resolution isn\'t available yet today.</p>' +
                            '<span class="curve-preview-message-sub">Try Daily or Monthly.</span>' +
                            '</div>',
                    )
                    setMeta({})
                }
                return
            }

            if (!res.ok) throw new Error(`Unexpected status ${res.status}`)

            const snapshot = (await res.json()) as CurveSnapshot
            await render(snapshot, resolution, seq)
        } catch {
            if (seq === requestSeq) {
                showStatus(
                    '<div class="curve-preview-message curve-preview-message--error">' +
                        '<p>Could not load curve data.</p>' +
                        '<button type="button" class="curve-preview-retry">Retry</button>' +
                        '</div>',
                )
                setMeta({})
                const retryBtn = statusEl.querySelector<HTMLButtonElement>('.curve-preview-retry')
                retryBtn?.addEventListener(
                    'click',
                    () => load(resolution),
                    { once: true },
                )
                retryBtn?.focus()
            }
        }
    }

    const activeResolution = (): Resolution => {
        const active = tabs.find((t) => t.classList.contains('active'))
        // Defensive fallback if no tab is marked active in the markup; mirrors
        // the blade default (Hourly is the active preview tab).
        return (active?.dataset.resolution as Resolution) ?? 'hour'
    }

    tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            const res = tab.dataset.resolution as Resolution
            // Check before re-classing: once `active` moves to the clicked
            // tab there is nothing left to compare against.
            if (!res || tab.classList.contains('active')) return

            tabs.forEach((t) => {
                t.classList.remove('active')
                t.setAttribute('aria-selected', 'false')
            })
            tab.classList.add('active')
            tab.setAttribute('aria-selected', 'true')
            void load(res)
        })
    })

    const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0]?.isIntersecting) {
                void load(activeResolution())
                observer.disconnect()
            }
        },
        { rootMargin: '200px' },
    )
    observer.observe(section)
}

// Guarded so the module can be imported in a non-DOM (test) environment.
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCurvePreview)
    } else {
        initCurvePreview()
    }
}
